import React, { useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch, RouteComponentProps, useHistory } from "react-router-dom";
import { IonApp, setupIonicReact } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "../theme/variables.css";

import Login from "./Login";
import SignUp from "./SignUp";
import ResetPassword from "./ResetPassword";
import EmailVerification from "./EmailVerification";
import ProfileSetup from "./ProfileSetup";
import LearningProfileSetup, { LEARNING_SKIP_KEY } from "./LearningProfileSetup";
import PhoneLogin from "./PhoneLogin";
import AuthCallback from "./AuthCallback";
import LoadingScreen from "./LoadingScreen";
import Home from "./Home";
import { StudentProvider, useStudent } from "../contexts/StudentContext";
import { PremiumProvider } from "../contexts/PremiumContext";
import { identify, reset, track, consumeOpenSource } from "../services/analytics";
import { initializePurchases, syncPremiumStatus, purchasesAvailable } from "../services/purchasesManager";
import { auth, usingEmulators } from "../firebase";
import type { User } from "firebase/auth";

setupIonicReact({ mode: "md" });

if (Capacitor.getPlatform() === "android") {
  StatusBar.setStyle({ style: Style.Dark }).catch(() => undefined);
  StatusBar.setBackgroundColor({ color: "#0f1730" }).catch(() => undefined);
  StatusBar.setOverlaysWebView({ overlay: true }).catch(() => undefined);
}

/** Vérification d'e-mail exigée seulement pour les comptes qui en ont un (pas invité, pas téléphone). */
export function needsEmailVerification(user: User): boolean {
  if (usingEmulators || user.isAnonymous || !user.email) return false;
  const hasPassword = user.providerData.some((p) => p.providerId === "password");
  return hasPassword && !user.emailVerified;
}

/**
 * Deep links natifs (retour Discord : scholeo://auth/discord?code=...). Rendu DANS le
 * BrowserRouter pour disposer de l'historique racine. Le navigateur système est refermé.
 */
const DeepLinks: React.FC = () => {
  const history = useHistory();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let remove: (() => void) | undefined;
    const handle = async (url: string | null | undefined) => {
      if (!url) return;
      const m = url.match(/^scholeo:\/\/auth\/discord\?(.*)$/);
      if (!m) return;
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.close();
      } catch (e) {
        /* déjà fermé */
      }
      history.push(`/auth/discord/callback?${m[1]}`);
    };
    import("@capacitor/app").then(({ App: CapApp }) => {
      CapApp.getLaunchUrl().then((r) => handle(r?.url)).catch(() => undefined);
      CapApp.addListener("appUrlOpen", ({ url }) => handle(url)).then((h) => (remove = () => h.remove()));
    });
    return () => remove && remove();
  }, [history]);
  return null;
};

/**
 * Guards (même logique que GameTime/App.jsx, en fonctionnel) :
 *  - non connecté            -> /login
 *  - e-mail non vérifié      -> /email-verification (comptes e-mail/mot de passe uniquement)
 *  - profil absent (année)   -> /profile-setup
 *  - élève sans profil d'apprentissage -> /learning-profile (une seule fois : « Plus tard »
 *    enregistre les défauts, et un marqueur de session évite d'insister si l'écriture échoue)
 *  - sinon                   -> /home (routeur Ionic interne)
 */
function learningSkippedThisSession(): boolean {
  try {
    return sessionStorage.getItem(LEARNING_SKIP_KEY) === "1";
  } catch (e) {
    return false;
  }
}

const Routes: React.FC = () => {
  const { user, authLoading, profile, profileLoading, isParent } = useStudent();

  useEffect(() => {
    if (user) {
      identify(user.uid);
      if (purchasesAvailable() && !user.isAnonymous) initializePurchases(user);
    } else {
      reset();
    }
  }, [user]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let remove: (() => void) | undefined;
    import("@capacitor/app").then(({ App: CapApp }) => {
      CapApp.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) return;
        track("app_open", { source: consumeOpenSource() });
        if (auth.currentUser && purchasesAvailable()) syncPremiumStatus(auth.currentUser).catch(() => undefined);
      }).then((h) => (remove = () => h.remove()));
    });
    return () => remove && remove();
  }, []);

  const signedInOnly = (render: (p: RouteComponentProps) => React.ReactNode) => (props: RouteComponentProps) => {
    if (authLoading) return <LoadingScreen />;
    if (!user) return <Redirect to={{ pathname: "/login", state: { from: props.location } }} />;
    if (needsEmailVerification(user)) return <Redirect to="/email-verification" />;
    if (profileLoading) return <LoadingScreen />;
    if (!profile) return <Redirect to="/profile-setup" />;
    if (!isParent && !profile.learningProfile && !learningSkippedThisSession()) return <Redirect to="/learning-profile" />;
    return render(props);
  };

  const learningProfileOnly = () => {
    if (authLoading || (user && profileLoading)) return <LoadingScreen />;
    if (!user) return <Redirect to="/login" />;
    if (needsEmailVerification(user)) return <Redirect to="/email-verification" />;
    if (!profile) return <Redirect to="/profile-setup" />;
    if (isParent || profile.learningProfile || learningSkippedThisSession()) return <Redirect to="/home" />;
    return <LearningProfileSetup mode="onboarding" />;
  };

  const notSignedInOnly = (render: () => React.ReactNode) => () => {
    if (authLoading) return <LoadingScreen />;
    if (user) return <Redirect to="/home" />;
    return render();
  };

  const verificationOnly = () => {
    if (authLoading) return <LoadingScreen />;
    if (!user) return <Redirect to="/login" />;
    return <EmailVerification />;
  };

  const profileSetupOnly = () => {
    if (authLoading || (user && profileLoading)) return <LoadingScreen />;
    if (!user) return <Redirect to="/login" />;
    if (needsEmailVerification(user)) return <Redirect to="/email-verification" />;
    if (profile) return <Redirect to="/home" />;
    return <ProfileSetup />;
  };

  return (
    <BrowserRouter>
      <DeepLinks />
      <Switch>
        <Route path="/login" render={notSignedInOnly(() => <Login />)} />
        <Route path="/signup" render={notSignedInOnly(() => <SignUp />)} />
        <Route path="/phone" render={notSignedInOnly(() => <PhoneLogin />)} />
        <Route path="/reset-password" render={notSignedInOnly(() => <ResetPassword />)} />
        <Route path="/auth/email-link" render={() => <AuthCallback kind="email-link" />} />
        <Route path="/auth/discord/callback" render={() => <AuthCallback kind="discord" />} />
        <Route path="/email-verification" render={verificationOnly} />
        <Route path="/profile-setup" render={profileSetupOnly} />
        <Route path="/learning-profile" render={learningProfileOnly} />
        <Route path="/home" render={signedInOnly(() => <Home />)} />
        <Route path="/">
          <Redirect to="/home" />
        </Route>
      </Switch>
    </BrowserRouter>
  );
};

const App: React.FC = () => (
  <IonApp>
    <StudentProvider>
      <PremiumProvider>
        <Routes />
      </PremiumProvider>
    </StudentProvider>
  </IonApp>
);

export default App;
